import React, { useState, useCallback, useMemo, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { toJS } from "mobx";

import "../../../forms-common.scss";
import {
  ActionsSection,
  ButtonsSection,
} from "../../../components/data-components/sections/DataSections";
import {
  RemoveUserFromProject,
  ViewProjectTeamMembers,
  ViewTeamMembersFromAssignedProject,
} from "../../../services/authorization/Permissions";
import PageLayout from "../../_layout/PageLayout";
import DataGrid from "../../../components/datatable/DataGrid";
import { AddExistingUsersButton } from "./components/buttons/AddExistingUsersButton";
import { ProjectMenuItemsModel } from "../ProjectMenuItems";
import RemoveButton from "../../../components/buttons/remove-button/RemoveButton";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import { ProjectUsersStorageModel } from "../../../services/user/ProjectUsersStorage";
import { api } from "../../../services/api/ApiService";
import useUrlParams from "../../../services/url/UrlParamsService";
import { CurrentProjectStorageModel } from "../../../services/project/CurrentProjectStorage";
import { UserRolesStorageModel } from "../../../services/user/UserRolesStorage";
import { useHistory } from "react-router";
import { EntityTypeFromPath } from "../../../services/breadcrumbs/EntityTypeFromPathModel";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar } = useErrorHandler();

  const projectId = useMemo(() => urlParamsService.getProjectId()!, [urlParamsService]);

  const [currentProjectStorage] = useState(() =>
    CurrentProjectStorageModel.create({ projectId: projectId }, { api })
  );

  const [userRolesStorage] = useState(() => UserRolesStorageModel.create({}, { api }));

  const [projectsUsersStorage] = useState(() =>
    ProjectUsersStorageModel.create(
      { projectId: projectId, useGridFiltering: true, autoLoad: false },
      { api }
    )
  );

  useEffect(() => {
    if (userRolesStorage.isLoading) {
      return;
    }

    projectsUsersStorage.setUserRoleOptions(toJS(userRolesStorage.userRoles));

    async function load() {
      await projectsUsersStorage.load();
    }
    load();
  }, [userRolesStorage.isLoading, userRolesStorage.userRoles, projectsUsersStorage]);

  const projectMenuItems = useMemo(() => {
    return ProjectMenuItemsModel.create({
      projectId: projectId,
      isLoading: currentProjectStorage.isLoading,
      numberOfSewerObjects: currentProjectStorage.currentProject.sewerObjectsCount,
    });
  }, [projectId, currentProjectStorage.isLoading, currentProjectStorage.currentProject.sewerObjectsCount]);

  const menuItems = {
    staticItems: projectMenuItems.getStaticItems(),
  };

  const isRemoveDisabled = useCallback(
    () =>
      !permissionsService.userHasPermissionTo(RemoveUserFromProject) ||
      !projectsUsersStorage.selectedItemsCount,
    [projectsUsersStorage.selectedItemsCount]
  );

  const removeSelectedUsers = useCallback(async () => {
    try {
      await projectsUsersStorage.removeUsersFromProject(projectsUsersStorage.selectedItems);
    } catch (error) {
      const errorContext = { itemsCount: projectsUsersStorage.selectedItemsCount };
      const applicationError = error as ApplicationError;
      applicationError.withContext(errorContext);
      applicationError.withErrorMessage("default-error.users.remove-from-project");
      handleErrorInSnackbar(applicationError);
    }
  }, [projectsUsersStorage, handleErrorInSnackbar]);

  useEffect(() => {
    breadcrumbsStorage.setEntityNames([{
      entity: EntityTypeFromPath.Project,
      name: `${currentProjectStorage.currentProject.name}`
    }]);

    breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
  }, [currentProjectStorage.currentProject.name, history.location.pathname]);

  return (
    <div>
      <PageLayout
        accessWithOneOfThePermissions={[ViewProjectTeamMembers, ViewTeamMembersFromAssignedProject]}
        titleKey="ProjectTeamMembers.title"
        titleContext={{
          projectName:
            currentProjectStorage.currentProject && currentProjectStorage.currentProject.name,
        }}
        menuItems={menuItems}
      >
        <ActionsSection>
          <ButtonsSection>
            <AddExistingUsersButton projectUsersStorage={projectsUsersStorage} />
            <RemoveButton
              disabled={isRemoveDisabled()}
              confirmationAction={removeSelectedUsers}
              confirmationModalTitleKey="UserProjects.remove.confirmation.title"
              confirmationModalTitleContext={{
                usersCount: projectsUsersStorage.selectedItemsCount,
              }}
              confirmationModalSubtitleKey="UserProjects.remove.confirmation"
            />
          </ButtonsSection>
        </ActionsSection>
        <DataGrid itemTypeKey="ProjectTeamMembers.table" dataGridStorage={projectsUsersStorage} />
      </PageLayout>
    </div>
  );
});
